import { type FormArray, type FormGroup } from '@angular/forms';

import {
  type ApiActionDef,
  type ApiDef,
  type ApiParameter,
} from '@blaze-shared/api-contracts-domain';
import { isEqual } from '@blaze-shared/shared-util';

import { type ParameterDataType } from '../api-builder.model';

export function updateDynamicFormControls<Form extends FormArray, Param extends ApiParameter>(
  form: Form,
  params: Param[],
  createNewParamField: (param?: Param) => FormGroup,
): Form {
  const delta = params?.length - form?.length;

  if (Number.isInteger(delta) && delta > 0) {
    for (let i = 0; i < delta; i += 1) {
      form.push(createNewParamField(), { emitEvent: false });
    }
  } else if (Number.isInteger(delta) && delta < 0) {
    for (let i = 0; i > delta; i -= 1) {
      form.removeAt(form.controls.length - 1, { emitEvent: false });
    }
  }

  form.patchValue(params, { emitEvent: false });

  const last = params[params.length - 1];
  const lastElementHasContent = !!(last && (last.name || last.value));
  if (!last || lastElementHasContent) {
    form.push(createNewParamField(), { emitEvent: false });
  }

  return form;
}

function areParametersEqual(
  params1: ApiParameter[] | undefined,
  params2: ApiParameter[] | undefined,
): boolean {
  if (!params1 && !params2) {
    return true;
  }
  if (!params1 || !params2) {
    return false;
  }
  if (params1.length !== params2.length) {
    return false;
  }

  const keysToCompare: (keyof ApiParameter)[] = ['name', 'description', 'displayName', 'value'];

  return params1.every((param1, index) => {
    const param2 = params2[index];
    return keysToCompare.every((key) => isEqual(param1[key] || null, param2[key] || null));
  });
}

function areActionsEqual(
  actions1: ApiActionDef[] | undefined,
  actions2: ApiActionDef[] | undefined,
): boolean {
  if (!actions1 && !actions2) {
    return true;
  }
  if (!actions1 || !actions2) {
    return false;
  }
  if (actions1.length !== actions2.length) {
    return false;
  }

  const keysToCompare: (keyof ApiActionDef)[] = [
    'body',
    'errorResultPath',
    'headers',
    'bodyParameter',
    'httpMethod',
    'name',
    'parameters',
    'resultPath',
    'successStatusCode',
    'url',
    'urlParameters',
    'noContent',
    'resultColumns',
  ];

  return actions1.every((action1, index) => {
    const action2 = actions2[index];
    return keysToCompare.every((key) => {
      if (key === 'urlParameters') {
        return areParametersEqual(action1.urlParameters, action2.urlParameters);
      } else if (key === 'headers') {
        return areParametersEqual(action1.headers, action2.headers);
      }

      return isEqual(action1[key] || null, action2[key] || null);
    });
  });
}

export function areApiDefsEqual(
  savedApiDef: ApiDef | undefined,
  currentApiDef: ApiDef | undefined,
): boolean {
  if (!savedApiDef && !currentApiDef) {
    return true;
  }
  if (!savedApiDef || !currentApiDef) {
    return false;
  }

  const keysToCompare: (keyof ApiDef)[] = [
    'baseUrl',
    'basicAuthorization',
    'oAuth2Authorization',
    'apiAuthorizationType',
    'bearerTokenAuthorization',
  ];

  const apiDefEqual = keysToCompare.every((key) => {
    const value1 = savedApiDef[key];
    const value2 = currentApiDef[key];
    return isEqual(value1 ?? null, value2 ?? null);
  });
  const headersEqual = areParametersEqual(savedApiDef.headers, currentApiDef.headers);
  const urlParametersEqual = areParametersEqual(
    savedApiDef.urlParameters,
    currentApiDef.urlParameters,
  );
  const actionsEqual = areActionsEqual(savedApiDef.actions, currentApiDef.actions);

  return [apiDefEqual, headersEqual, urlParametersEqual, actionsEqual].every(Boolean);
}

export function toParameterDataType(value: string | undefined): ParameterDataType | undefined {
  if (
    value === 'array' ||
    value === 'boolean' ||
    value === 'datetime' ||
    value === 'date' ||
    value === 'file' ||
    value === 'number' ||
    value === 'string' ||
    value === 'time'
  ) {
    return value;
  }
  return undefined;
}
